import { useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { DISTRIBUTOR, loadJson, useSiteInit } from '@autopass-web/domain';
import FeatureMaintenance from '@autopass-web/feature-maintenance';
import { AppMessage } from '@autopass-web/mochi-component';

import { CustomerServiceModal } from '../components/customer-service-modal';
import RefuelingRequestDialog from '../components/refueling-request-dialog';
import Toast from '../components/toast';
import { DIST, dynamicImportComponentsPath, helperUrl, PATH } from '../config';
import { DenDenWrapper } from '../layout/denden-wrapper';
import { EnterpriseJkoWrapper } from '../layout/enterprise-jko-wrapper';
import { EnterprisePiWrapper } from '../layout/enterprise-pi-wrapper';
import { PlusPayWrapper } from '../layout/plus-pay-wrapper';
import { ToyotaLexusWrapper } from '../layout/toyota-lexus-wrapper';
import Wrapper from '../layout/wrapper';
import amplitude from '../service/amplitude';
import { openLink } from '../utils/link-helper';
import useAppStore from './use-app';

import './styles.sass';
import '../utils/version-log';

export let loveCodeList = [];

const queryClient = new QueryClient();

const App = ({ Component, pageProps }) => {
  const appStore = useAppStore();
  const router = useRouter();

  // 地圖＆快速通頁面為載入 libs 的頁面，內部的 routing 跟 client-mochipay 的 history 是分開的
  useEffect(() => {
    window.onpopstate = () => {
      if (
        dynamicImportComponentsPath.some((path) =>
          window.location.href.includes(path),
        )
      ) {
        router.push(window.location.pathname);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSiteInit();

  useEffect(() => {
    amplitude.init();
  }, []);

  useEffect(() => {
    if (!appStore.distributor) return;

    loadJson('/json/love_code.json').then((data) => {
      loveCodeList = data;
    });
  }, [appStore.distributor]);

  const pageTitle = DIST === DISTRIBUTOR.RICHART_LIFE ? '停車專區' : 'Autopass';

  let container;

  switch (DIST) {
    case DISTRIBUTOR.ENTERPRISE_PI:
      container = (
        <EnterprisePiWrapper>
          <Component {...pageProps} />
        </EnterprisePiWrapper>
      );
      break;
    case DISTRIBUTOR.ENTERPRISE_JKO:
      container = (
        <EnterpriseJkoWrapper>
          <Component {...pageProps} />
        </EnterpriseJkoWrapper>
      );
      break;
    case DISTRIBUTOR.MYTOYOTA:
    case DISTRIBUTOR.LEXUS_PLUS:
      container = (
        <ToyotaLexusWrapper>
          <Component {...pageProps} />
        </ToyotaLexusWrapper>
      );
      break;
    case DISTRIBUTOR.DENDEN:
      container = (
        <DenDenWrapper>
          <Component {...pageProps} />
        </DenDenWrapper>
      );
      break;
    case DISTRIBUTOR.PLUS_PAY:
      container = (
        <PlusPayWrapper>
          <Component {...pageProps} />
        </PlusPayWrapper>
      );
      break;
    default:
      container = (
        <Wrapper>
          <Component {...pageProps} />
        </Wrapper>
      );
      break;
  }

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no,viewport-fit=cover, interactive-widget=resizes-content"
        />
        <meta name="theme-color" content="#F1F1F6" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      </Head>
      <RefuelingRequestDialog />
      <CustomerServiceModal
        isOpen={appStore.customerServiceDialog}
        onHelpLinkClick={() =>
          openLink(appStore.distributor.params.deeplink?.helper || helperUrl)
        }
        onContactServiceClick={() => {
          appStore.toggleCustomerServiceDialog();
          if (appStore.me) {
            router.push({
              pathname: PATH.CONTACT,
              query: {
                name: appStore.me?.displayName || undefined,
                mobile: appStore.me?.mobile,
                email: appStore.me?.email,
                issueGroupKey: 'Account',
              },
            });
          } else {
            router.push(PATH.CONTACT);
          }
        }}
        onClose={appStore.toggleCustomerServiceDialog}
      />
      <Toast
        isShow={appStore.toastMessage !== ''}
        text={appStore.toastMessage}
      />
      <AppMessage
        type={appStore.appMessage.type}
        show={appStore.appMessageShow}
        onDismiss={() => {
          if (appStore.appMessage.onClick) {
            appStore.appMessage.onClick();
          }
          appStore.toggleAppMessage(false);
        }}
        message={appStore.appMessage.message}
      />
      <QueryClientProvider client={queryClient}>
        <FeatureMaintenance>{container}</FeatureMaintenance>
      </QueryClientProvider>
    </>
  );
};
export default App;
